import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from 'sonner';
import { useClients, useCreateLoan } from '@/hooks/use-api';
import { Loader2 } from 'lucide-react';
interface LoanWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}
export function LoanWizard({ open, onOpenChange }: LoanWizardProps) {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    clientId: '',
    amount: '',
    duration: '',
    guarantorName: '',
    guarantorPhone: '',
    relationship: ''
  });
  const { data: clientsData, isLoading: clientsLoading } = useClients();
  const createLoan = useCreateLoan();
  const totalSteps = 3;
  const handleNext = async () => {
    if (step < totalSteps) {
      if (step === 1 && (!formData.clientId || !formData.amount)) {
        toast.error("Please fill in client details");
        return;
      }
      setStep(step + 1);
    } else {
      try {
        await createLoan.mutateAsync({
          clientId: formData.clientId,
          amount: parseFloat(formData.amount),
          duration: `${formData.duration} months`,
          status: 'Pending',
          progress: 0
        });
        toast.success("Loan application submitted for review");
        onOpenChange(false);
        resetForm();
      } catch (e) {
        toast.error("Failed to submit application");
      }
    }
  };
  const resetForm = () => {
    setStep(1);
    setFormData({
      clientId: '',
      amount: '',
      duration: '',
      guarantorName: '',
      guarantorPhone: '',
      relationship: ''
    });
  };
  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] rounded-2xl border-border">
        <DialogHeader>
          <DialogTitle className="text-foreground font-black uppercase tracking-tight">New Loan Application</DialogTitle>
          <DialogDescription className="text-muted-foreground font-bold uppercase tracking-widest text-[10px]">
            Step {step} of {totalSteps}: {
              step === 1 ? "Client Details" : step === 2 ? "Guarantor Information" : "Documentation"
            }
          </DialogDescription>
        </DialogHeader>
        <div className="py-4 space-y-4">
          {step === 1 && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-foreground font-bold">Select Client</Label>
                <Select
                  value={formData.clientId}
                  onValueChange={(val) => setFormData(p => ({ ...p, clientId: val }))}
                >
                  <SelectTrigger className="rounded-xl border-border bg-muted/30">
                    <SelectValue placeholder={clientsLoading ? "Loading clients..." : "Choose a client"} />
                  </SelectTrigger>
                  <SelectContent className="rounded-xl">
                    {clientsData?.items.map(c => (
                      <SelectItem key={c.id} value={c.id} className="text-foreground">{c.name} ({c.phone})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-foreground font-bold">Amount (GHS)</Label>
                  <Input
                    type="number"
                    placeholder="5000"
                    className="rounded-xl border-border bg-muted/30"
                    value={formData.amount}
                    onChange={(e) => setFormData(p => ({ ...p, amount: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-foreground font-bold">Duration (Months)</Label>
                  <Input
                    type="number"
                    placeholder="6"
                    className="rounded-xl border-border bg-muted/30"
                    value={formData.duration}
                    onChange={(e) => setFormData(p => ({ ...p, duration: e.target.value }))}
                  />
                </div>
              </div>
            </div>
          )}
          {step === 2 && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-foreground font-bold">Guarantor Full Name</Label>
                <Input
                  placeholder="John Doe"
                  className="rounded-xl border-border bg-muted/30"
                  value={formData.guarantorName}
                  onChange={(e) => setFormData(p => ({ ...p, guarantorName: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-foreground font-bold">Relationship</Label>
                <Input
                  placeholder="Employer / Family"
                  className="rounded-xl border-border bg-muted/30"
                  value={formData.relationship}
                  onChange={(e) => setFormData(p => ({ ...p, relationship: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-foreground font-bold">Guarantor Phone</Label>
                <Input
                  placeholder="024 000 0000"
                  className="rounded-xl border-border bg-muted/30"
                  value={formData.guarantorPhone}
                  onChange={(e) => setFormData(p => ({ ...p, guarantorPhone: e.target.value }))}
                />
              </div>
            </div>
          )}
          {step === 3 && (
            <div className="space-y-4 bg-muted/50 p-4 rounded-2xl border border-border/50">
              <p className="text-[10px] font-black text-muted-foreground uppercase tracking-widest mb-2 px-1">Checklist of required documents:</p>
              {[
                "Valid Government ID Card",
                "Two Recent Passport Photos",
                "Proof of Residence (Utility Bill)",
                "Business Registration (if applicable)"
              ].map((item) => (
                <div key={item} className="flex items-center space-x-2">
                  <Checkbox id={item} className="border-border data-[state=checked]:bg-primary data-[state=checked]:border-primary" />
                  <label htmlFor={item} className="text-xs font-bold text-foreground leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 uppercase tracking-tight">{item}</label>
                </div>
              ))}
            </div>
          )}
        </div>
        <DialogFooter className="flex justify-between sm:justify-between items-center w-full pt-2">
          <Button variant="ghost" onClick={handleBack} disabled={step === 1} className="rounded-xl text-muted-foreground hover:bg-muted/50 font-bold uppercase text-[10px]">Back</Button>
          <Button
            className="bg-primary hover:bg-primary/90 rounded-xl px-8 font-black uppercase tracking-tight shadow-lg shadow-primary/20"
            onClick={handleNext}
            disabled={createLoan.isPending}
          >
            {createLoan.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            {step === totalSteps ? "Submit Application" : "Continue"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}